import React, { useEffect, useState } from "react";
import { publicLink } from "@/lib/landerDomain";
import { toast } from "sonner";
import { ArrowSquareOut, Checks, Desktop, DeviceMobile } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TestLeadResult } from "@/components/SendTestLeadButton";
import { AdminModal } from "@/components/pageSettings/AdminModal";
import { previewUrl } from "@/components/thankyou/ThankYouPreviewDialog";
import { picksLabel } from "@/lib/funnelOptions";
import { LeadSourceRow } from "./LeadSourceRow";
import { CheckAllPagesModal } from "./CheckAllPagesModal";
import { BuyerPageActions } from "./BuyerPageActions";

const testBody = (page, row) => ({ buyer_page_id: page.page_id, ...(row.kind === "own" ? { page_slug: row.key } : { funnel: row.key }) });
const errText = (e) => e?.response?.data?.detail || "Couldn't send the test lead";

const PILL = "border-2 border-black px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 transition-colors duration-100";
const pill = (on) => `${PILL} ${on ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`;

function ThankYouModal({ view, onClose }) {
  const [phone, setPhone] = useState(false);
  const { page, row, result } = view;
  const url = result ? result.thank_you_path
    : previewUrl(page.code, row.ty_funnel, undefined, row.has_ty_experience ? row.ty_experience : undefined);
  return (
    <AdminModal eyebrow={result ? "Test lead sent" : "Thank-you page preview"} title={`${page.firm_name} · ${row.title}`} onClose={onClose} testId="lead-sources-ty-modal" wide>
      {result ? (
        <div className="mb-2 space-y-1.5" data-testid="lead-sources-test-result">
          <TestLeadResult r={result} showLeadLink testId="lead-sources-test" />
          <p className="text-[11px] text-zinc-600">Below is the thank-you page this test lead showed ({row.thank_you_design}). It's marked as a test, so nothing is tracked.</p>
        </div>
      ) : (
        <p className="mb-2 text-[11px] text-zinc-600">
          Exactly what a visitor sees after a lead from <b>{row.title}</b> goes to {page.firm_name} ({row.thank_you_design}). Nothing is tracked or counted here.
        </p>
      )}
      <div className="mb-2 flex flex-wrap justify-end gap-1.5">
        <button type="button" onClick={() => setPhone(false)} className={pill(!phone)} data-testid="lead-sources-ty-desktop"><Desktop size={11} weight="bold" /> Desktop</button>
        <button type="button" onClick={() => setPhone(true)} className={pill(phone)} data-testid="lead-sources-ty-phone"><DeviceMobile size={11} weight="bold" /> Phone</button>
        <a href={publicLink(url)} target="_blank" rel="noopener noreferrer" className={pill(false)} data-testid="lead-sources-ty-open-tab"><ArrowSquareOut size={11} weight="bold" /> New tab</a>
      </div>
      <div className="flex justify-center bg-zinc-100 p-2">
        <iframe key={url} title="Thank-you page" src={publicLink(url)} data-testid="lead-sources-ty-frame"
                className={`h-[70vh] border-2 border-black bg-white ${phone ? "w-[390px]" : "w-full"}`} />
      </div>
    </AdminModal>
  );
}

function PageBlock({ page, onThankYou, onTest, onCheckAll, testing, onChanged }) {
  const p = `lead-sources-${page.code}`;
  return (
    <div className="border-2 border-black bg-white" data-testid={p}>
      <div className="flex flex-wrap items-center gap-2 border-b-2 border-black bg-zinc-100 px-3 py-2 text-xs">
        <b>{page.firm_name}</b>
        <span className="font-mono text-[10px] text-zinc-600">buyer code {page.code}</span>
        {page.status !== "published" && <span className="border border-amber-700 px-1 text-[9px] uppercase tracking-widest text-amber-800">{page.status}</span>}
        <span className="ml-auto text-[10.5px] text-zinc-600" data-testid={`${p}-picks`}>Shared pages: {picksLabel(page.picks)}</span>
        {onCheckAll && (
          <button type="button" onClick={() => onCheckAll(page)} disabled={Boolean(testing)} data-testid={`${p}-check-all`}
                  className={`${PILL} bg-amber-300 hover:bg-black hover:text-white disabled:opacity-50`}>
            <Checks size={11} weight="bold" /> Check all pages
          </button>
        )}
        {onChanged && <BuyerPageActions page={page} onChanged={onChanged} />}
      </div>
      {page.rows.map((r) => (
        <LeadSourceRow key={r.key} row={r} p={`${p}-${r.key}`} onThankYou={(row) => onThankYou({ page, row })}
                       onTest={onTest && ((row) => onTest(page, row))} testing={testing === `${page.code}-${r.key}` || testing === "all"} />
      ))}
    </div>
  );
}

// Manage Buyer: every page that can send this buyer leads, how many it sent, and the thank-you page each one shows.
export function LeadSourcesPanel({ orgId, onChanged }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [view, setView] = useState(null);
  const [testing, setTesting] = useState("");
  const [tick, setTick] = useState(0);
  const [batch, setBatch] = useState(null);
  const { user } = useAuth();
  const canTest = ["super_admin", "admin"].includes(user?.role);
  const canEdit = user?.org_type === "internal" && ["super_admin", "admin", "employee"].includes(user?.role);
  const reload = () => { setTick((t) => t + 1); onChanged?.(); };
  useEffect(() => {
    api.get(`/admin/clients/${orgId}/lead-sources`, { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setData({ failed: true, pages: [], other: {} }));
  }, [orgId, days, tick]);
  const runTest = async (page, row) => {
    if (!window.confirm(`Send a TEST lead from "${row.title}" to ${page.firm_name}'s real delivery? It's marked as a test and kept out of billing and reports.`)) return;
    setTesting(`${page.code}-${row.key}`);
    try {
      const { data: r } = await api.post("/admin/global-funnels/test-lead", testBody(page, row));
      setView({ page, row, result: r });
      setTick((t) => t + 1);
      toast.success(`Test lead sent via ${r.funnel_title}`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setTesting("");
    }
  };
  const runAll = async (page) => {
    const rows = page.rows.filter((r) => r.picked && r.kind !== "direct");
    if (!rows.length) return;
    if (!window.confirm(`Send ${rows.length} TEST leads, one from each of ${page.firm_name}'s pages, to their real delivery? They're marked as tests and kept out of billing and reports.`)) return;
    const set = (i, patch) => setBatch((b) => ({ ...b, items: b.items.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
    setBatch({ page, items: rows.map((row) => ({ row, state: "waiting" })) });
    setTesting("all");
    for (let i = 0; i < rows.length; i += 1) {
      try {
        const { data: r } = await api.post("/admin/global-funnels/test-lead", testBody(page, rows[i]));
        set(i, { state: "sent", result: r });
      } catch (e) {
        set(i, { state: "error", error: errText(e) });
      }
    }
    setTesting("");
    setTick((t) => t + 1);
  };
  return (
    <div className="space-y-3" data-testid="lead-sources">
      <div className="flex flex-wrap items-center gap-1.5">
        {[7, 30, 90].map((d) => (
          <button key={d} type="button" onClick={() => { if (d !== days) { setData(null); setDays(d); } }} className={pill(d === days)} data-testid={`lead-sources-days-${d}`}>Last {d} days</button>
        ))}
      </div>
      {!data && <div className="label-mono">Loading…</div>}
      {data?.failed && <div className="text-xs text-rose-700" data-testid="lead-sources-error">Couldn&apos;t load lead sources. Refresh to try again.</div>}
      {data && !data.failed && !data.pages.length && (
        <div className="border-2 border-dashed border-zinc-400 p-3 text-xs text-zinc-600" data-testid="lead-sources-no-page">
          {data.has_delivery ? "This buyer has no landing page yet, so no shared or own pages send them leads." : "This buyer has no delivery set up, so no pages can send them leads yet."}
        </div>
      )}
      {(data?.pages || []).map((pg) => (
        <PageBlock key={pg.page_id} page={pg} onThankYou={setView} onTest={canTest ? runTest : null}
                   onCheckAll={canTest ? runAll : null} testing={testing} onChanged={canEdit ? reload : null} />
      ))}
      {data && !data.failed && (
        <div className="border-2 border-black bg-white px-3 py-2 text-xs" data-testid="lead-sources-other">
          <b>Other sources</b> (partner feeds, API, imports): <b data-testid="lead-sources-other-count">{data.other.leads}</b> delivered
          {data.other.last_lead_at && <span className="text-zinc-500"> · last {new Date(data.other.last_lead_at).toLocaleString()}</span>}
        </div>
      )}
      <p className="text-[10.5px] text-zinc-500">
        Leads = leads from that page that were sent to this buyer, test leads included and marked. Delivered = reached one of their deliveries.
        &quot;Page&quot; and &quot;Thank-you page&quot; open previews that aren&apos;t counted.
        {canTest && " \u201cTest + thank-you\u201d sends a marked test lead from that page to their real delivery, then shows the thank-you page it showed. \u201cCheck all pages\u201d does that for every page they have and lists the results together."}
      </p>
      {view && <ThankYouModal view={view} onClose={() => setView(null)} />}
      {batch && <CheckAllPagesModal batch={batch} onClose={() => { if (testing !== "all") setBatch(null); }} />}
    </div>
  );
}
